import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import {
    NORMAL_GROUP_ID,
    ORDER_SET_GROUP_ID,
    GROUPABLE_COLUMN_IDS,
    COLUMN_LABELS,
    MAX_CUSTOM_GROUPS,
    getGroupDisplayName,
} from '../Orders/ordersColumnConfig';

const ColumnGroupsModal = ({
    show,
    onHide,
    activeGroupId,
    defaultGroupId,
    customGroups,
    onApply,
    onSaveCustomGroup,
    onDeleteCustomGroup,
    groupableColumnIds = GROUPABLE_COLUMN_IDS,
    columnLabels = COLUMN_LABELS,
    builtinGroups = [{ id: ORDER_SET_GROUP_ID, name: 'Order Set' }],
}) => {
    const [localActiveId, setLocalActiveId] = useState(NORMAL_GROUP_ID);
    const [localDefaultId, setLocalDefaultId] = useState(NORMAL_GROUP_ID);
    const [customName, setCustomName] = useState('');
    const [selectedColumnIds, setSelectedColumnIds] = useState([]);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const prevShowRef = useRef(false);

    useEffect(() => {
        if (show && !prevShowRef.current) {
            setLocalActiveId(activeGroupId || NORMAL_GROUP_ID);
            setLocalDefaultId(defaultGroupId || NORMAL_GROUP_ID);
            setCustomName('');
            setSelectedColumnIds([]);
            setEditingGroupId(null);
        }
        prevShowRef.current = show;
    }, [show, activeGroupId, defaultGroupId]);

    const toggleColumnSelection = (columnId) => {
        setSelectedColumnIds((prev) => {
            if (prev.includes(columnId)) return prev.filter((id) => id !== columnId);
            return [...prev, columnId];
        });
    };

    const startEditGroup = (group) => {
        setEditingGroupId(group.id);
        setCustomName(group.name || '');
        setSelectedColumnIds([...(group.columnIds || [])]);
    };

    const resetCustomForm = () => {
        setEditingGroupId(null);
        setCustomName('');
        setSelectedColumnIds([]);
    };

    const handleSaveCustom = () => {
        const name = customName.trim();
        if (!name || selectedColumnIds.length < 2) return;
        onSaveCustomGroup({
            id: editingGroupId || `custom-${Date.now()}`,
            name,
            columnIds: [...selectedColumnIds],
        });
        resetCustomForm();
    };

    const handleApply = () => {
        onApply({
            activeGroupId: localActiveId,
            defaultGroupId: localDefaultId,
        });
        onHide();
    };

    const allGroupOptions = [
        { id: NORMAL_GROUP_ID, name: 'Normal View', type: 'builtin' },
        ...builtinGroups.map((group) => ({ id: group.id, name: group.name, type: 'builtin' })),
        ...customGroups.map((g) => ({ id: g.id, name: g.name, type: 'custom' })),
    ];

    const builtinName = builtinGroups.find((group) => group.id === localDefaultId)?.name;
    const defaultLabel = localDefaultId === NORMAL_GROUP_ID
        ? 'Normal View'
        : (builtinName || getGroupDisplayName(localDefaultId, customGroups));

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="md"
            backdrop="static"
            contentClassName="d-flex flex-column column-groups-modal"
        >
            <Modal.Header
                className="column-groups-modal-header"
                style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                    padding: '15px 20px',
                    flexShrink: 0,
                }}
            >
                <Modal.Title style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#212529' }}>
                    COLUMN GROUPS
                </Modal.Title>
                <div className="d-flex align-items-center gap-2 ms-auto">
                    <span
                        className="badge bg-primary column-groups-default-badge"
                        title={`Default group: ${defaultLabel}`}
                    >
                        <span className="column-groups-default-badge-prefix">Default</span>
                        <span className="column-groups-default-badge-sep" aria-hidden="true">:</span>
                        <span className="column-groups-default-badge-name">{defaultLabel}</span>
                    </span>
                    <button type="button" className="btn-close" onClick={onHide} aria-label="Close" />
                </div>
            </Modal.Header>

            <Modal.Body style={{ padding: 0, overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                <div className="p-3">
                    <div className="small fw-bold text-muted text-uppercase mb-2">Select a group to apply</div>
                    {allGroupOptions.map((option, index) => {
                        const isActive = localActiveId === option.id;
                        const isDefault = localDefaultId === option.id;
                        return (
                            <div
                                key={option.id}
                                className="column-groups-option-row"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 15px',
                                    borderBottom: index < allGroupOptions.length - 1 ? '1px solid #e9ecef' : 'none',
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                }}
                            >
                                <label className="d-flex align-items-center gap-2 mb-0 flex-grow-1" style={{ cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="column-group-active"
                                        checked={isActive}
                                        onChange={() => setLocalActiveId(option.id)}
                                    />
                                    <span style={{ fontWeight: 500 }}>{option.name}</span>
                                    {isDefault && (
                                        <span className="badge bg-secondary column-groups-row-default-badge">Default</span>
                                    )}
                                </label>
                                <div className="d-flex gap-1 flex-shrink-0">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setLocalDefaultId(option.id)}
                                        title="Set as default"
                                    >
                                        Set Default
                                    </Button>
                                    {option.type === 'custom' && (
                                        <>
                                            <Button variant="outline-primary" size="sm" onClick={() => startEditGroup(customGroups.find((g) => g.id === option.id))}>
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => onDeleteCustomGroup(option.id)}
                                            >
                                                Delete
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <hr className="my-3" />

                    <div className="small fw-bold text-muted text-uppercase mb-2">
                        {editingGroupId ? 'Edit custom group' : 'Create custom group'}
                    </div>
                    <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Group name</Form.Label>
                        <Form.Control
                            type="text"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="e.g. Ship + Customer"
                            maxLength={60}
                        />
                    </Form.Group>
                    <div className="small text-muted mb-2">Select 2 or more columns to combine:</div>
                    <div className="column-groups-picker border rounded p-2 mb-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {groupableColumnIds.map((colId) => (
                            <Form.Check
                                key={colId}
                                type="checkbox"
                                id={`group-col-${colId}`}
                                label={columnLabels[colId] || colId}
                                checked={selectedColumnIds.includes(colId)}
                                onChange={() => toggleColumnSelection(colId)}
                                className="mb-1"
                            />
                        ))}
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={!customName.trim() || selectedColumnIds.length < 2 || (!editingGroupId && customGroups.length >= MAX_CUSTOM_GROUPS)}
                            onClick={handleSaveCustom}
                        >
                            {editingGroupId ? 'Update Group' : 'Save Group'}
                        </Button>
                        {editingGroupId && (
                            <Button variant="outline-secondary" size="sm" onClick={resetCustomForm}>
                                Cancel Edit
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer
                style={{
                    borderTop: '2px solid #dee2e6',
                    padding: '15px 20px',
                    backgroundColor: '#f8f9fa',
                    flexShrink: 0,
                }}
            >
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleApply}>
                    Apply
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ColumnGroupsModal;
